import { Headers, Method, callFetch } from "./";
import { toErrorPayload } from "../../../helpers/errorPayload";
import { SERVER_ERROR_MESSAGE, API_HOST_ERROR_MESSAGE } from "../../../constants/errorMessages";
import { ApplicationState } from "../../nissan_no";
import { FooterState, GetUpdatedNoFooterPrices } from "../../Footer";
import { Coverage } from "../../Insurance";
import { addCoverageDescriptionFromState, mapAddonsFromState, mapCoverageItemsInCoverage, mapCoveragePrice, mapDeductibles, preparePrice } from "./quote";
import { IAddOn } from "../../AddOn";

export const getPayload = (state: ApplicationState) => {
  const youngDriver = state.youngDriver?.selected ?? false;
  const selectedCoverage = state?.insurance?.coverage?.Coverages?.find(
    elem => elem.InternalCode === state?.insurance?.coverage?.selectedCode,
  );
  const selectedDeductible =
    state?.insurance?.coverage?.SelectedDeductible ?? selectedCoverage?.Deductibles?.find(d => d.IsSelected === true)?.Code;
  const milage = +(state.profile?.actualMilage?.value ?? 0);
  const cid = +(state.insurance?.coverage?.selectedCode ?? "0");
  const addons = state.addon?.selectedAddOnInternalCodes.map(i => +i) ?? [];
  const deductible = selectedDeductible;
  const distance = state.distance?.selectedId;
  const selectedBonusId = state.bonus?.bonus?.selectedId ?? null;

  return JSON.stringify({
    selectedCoverageId: cid,
    selectedDeductibleId: deductible,
    selectedAddonCoverageIds: addons,
    currentMileageInKilometers: milage,
    selectedBonusId: selectedBonusId,
    currentMileageBelow150000Kilometers: true,
    selectedMileageOptionId: distance,
    driverBelow23YearsOfAge: youngDriver,
    financingType: state.financingOptions?.financingTypes?.find(x => x.selected)?.name ?? "Other",
  });
};

const calculate = async (state: ApplicationState, dispatch?: Function): Promise<ApplicationState> => {
  const { apiHost, quoteId } = state.inputs;

  if (!apiHost) {
    if (dispatch)
      dispatch({
        type: "SET_SERVER_ERROR",
        payload: toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE }),
      });
    throw toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE });
  }

  const payload = getPayload(state);

  const qid = { quoteId: quoteId ?? "" };
  Object.assign(Headers, qid);

  const fetchBuilder = () =>
    fetch(`${apiHost}/api/no/${state.inputs.partner}/calculate`, {
      method: Method,
      mode: "cors",
      headers: Headers,
      body: payload,
    });

  const response = await callFetch(fetchBuilder);

  const json = await response.json();

  if (response.status !== 200) {
    const message = (json && json.message) ?? SERVER_ERROR_MESSAGE;
    if (dispatch)
      dispatch({
        type: "SET_SERVER_ERROR",
        payload: toErrorPayload({ message, header: SERVER_ERROR_MESSAGE }),
      });
    throw toErrorPayload({ message, header: SERVER_ERROR_MESSAGE });
  }

  const coverages = json.coverages.map(
    (val: any) =>
    ({
      InternalCode: val.id,
      LongName: val.name,
      ShortName: val.name.substring(0, 3),
      Description: addCoverageDescriptionFromState(val, state),
      PrimaryPrice: mapCoveragePrice(val.price),
      SecondaryPrice: {},
      IsRecommended: val.isSelected,
      CoverageItems: mapCoverageItemsInCoverage(json.coverageItems, val.includedCoverageItems),
      Deductibles: mapDeductibles(val.deductibles),
      CoverageAddons: mapAddonsFromState(val.addons, state),
      CalculatedPrice: preparePrice(val.price.find((x: any) => x.interval === "Monthly")),
      CalculatedPriceSecondary: "",
    } as Coverage),
  );

  const currentCoverage = coverages.find((x: any) => x.IsRecommended === true) as Coverage;
  const currentCoverageTax = json.vehicle?.insuranceTax?.find((x: any) => x.interval === "Monthly");

  const selectedAddonInternalCodes = currentCoverage.CoverageAddons?.filter((x: IAddOn) => x.Checked)
    ?.map((x: IAddOn) => x.InternalCode)
    ?.filter((x: any) => x != null) as string[];

  const footerPrices = GetUpdatedNoFooterPrices(state.footer as FooterState, currentCoverage, selectedAddonInternalCodes);

  return {
    ...state,
    addon: {
      ...state.addon,
      addOns: currentCoverage?.CoverageAddons,
      selectedAddOnInternalCodes: selectedAddonInternalCodes,
    },
    insurance: {
      ...state.insurance,
      coverage: {
        ...state.insurance?.coverage,
        selectedId: currentCoverage.InternalCode,
        selectedName: currentCoverage.LongName,
        Coverages: coverages,
      },
    },
    vehicleInsuranceTax: {
      amount: currentCoverageTax.amount,
      type: currentCoverageTax.interval,
      currency: currentCoverageTax.currency,
      text: "Trafikkforsikringsavgift til saten",
      readMoreText: "Las mere.",
      url: "https://if.no",
    },
    footer: {
      ...footerPrices,
    },
    payload,
  } as ApplicationState;
};

export default calculate;
